import { useMemo } from "react";
import { PlusCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/Button";
import { EventCard } from "@/components/EventCard";
import { useRecentEvents, useRecentsSync } from "@/hooks/useRecentEvents";
import { usePageMeta } from "@/hooks/usePageMeta";
import { avatarColor, initials } from "@/lib/avatar";
import { isPast } from "@/lib/dates";
import type { RecentEvent } from "@/lib/storage";
import { getChatPool, getRandomWindow } from "./homeChatPool";
import { HOME_USE_CASE_IDS, findUseCaseById, slugFor } from "./useCases";
import { getUseCaseContent } from "./useCasesContent";
import styles from "./Home.module.css";

const CHAT_WINDOW = 47;

function dedupByShortcut(entries: RecentEvent[]): RecentEvent[] {
  const map = new Map<string, RecentEvent>();
  for (const entry of entries) {
    const existing = map.get(entry.shortcut);
    if (!existing || entry.role === "admin") {
      map.set(entry.shortcut, entry);
    }
  }
  return [...map.values()];
}

function sortFuture(a: RecentEvent, b: RecentEvent) {
  return new Date(a.eventAt).getTime() - new Date(b.eventAt).getTime();
}

function sortPast(a: RecentEvent, b: RecentEvent) {
  return new Date(b.eventAt).getTime() - new Date(a.eventAt).getTime();
}

export function Home() {
  const { t } = useTranslation();
  const { entries } = useRecentEvents();
  useRecentsSync();

  usePageMeta({
    title: t("pageMeta.homeTitle"),
    description: t("pageMeta.homeDescription"),
  });

  const { future, past } = useMemo(() => {
    const deduped = dedupByShortcut(entries);
    return {
      future: deduped.filter((e) => !isPast(e.eventAt)).sort(sortFuture),
      past: deduped.filter((e) => isPast(e.eventAt)).sort(sortPast),
    };
  }, [entries]);

  return (
    <section className={styles.page}>
      <Hero />
      <Compare />
      <HeroCta />
      <UseCases />

      {future.length > 0 && (
        <section className={styles.events}>
          <div className={styles.listHeader}>
            <h2>{t("home.myEvents")}</h2>
            <Link to="/new" className={styles.headerCtaLink} aria-label={t("home.ctaCreate")}>
              <Button size="compact" variant="ghost">
                <PlusCircle size={16} weight="regular" />
              </Button>
            </Link>
          </div>
          <div className={styles.list}>
            {future.map((entry) => (
              <EventCard key={`${entry.shortcut}-${entry.role}`} entry={entry} />
            ))}
          </div>
        </section>
      )}

      {past.length > 0 && (
        <section className={styles.events}>
          <h3 className={styles.pastHeading}>{t("home.pastEvents")}</h3>
          <div className={styles.list}>
            {past.map((entry) => (
              <EventCard key={`${entry.shortcut}-${entry.role}`} entry={entry} />
            ))}
          </div>
        </section>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Hero — „Nie wieder [Chat-Chaos]." mit gelbem Marker unter dem Key-Word.
// ---------------------------------------------------------------------------

function Hero() {
  const { t } = useTranslation();
  return (
    <header className={styles.hero}>
      <h1 className={styles.heroTitle}>
        {t("home.heroTitlePrefix")}{" "}
        <span className={styles.heroMarker}>{t("home.heroTitleHighlight")}</span>
        {t("home.heroTitleSuffix")}
      </h1>
      <p className={styles.heroLead}>{t("home.heroLead")}</p>
    </header>
  );
}

function HeroCta() {
  const { t } = useTranslation();
  return (
    <div className={styles.heroCtaBlock}>
      <Link to="/new" className={styles.heroCtaLink}>
        <Button className={styles.heroCtaButton}>
          <PlusCircle size={20} weight="regular" />
          {t("home.ctaCreate")}
        </Button>
      </Link>
      <p className={styles.heroHints}>
        <span>{t("home.hint1")}</span>
        <span aria-hidden>·</span>
        <span>{t("home.hint2")}</span>
        <span aria-hidden>·</span>
        <span>{t("home.hint3")}</span>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Compare — VORHER/NACHHER side-by-side. Der Chat ist scrollbar, zeigt einen
// zufälligen ~47-Nachrichten-Ausschnitt aus einem ~250-Messages-Pool, sodass
// der Besucher bei jedem Reload etwas Neues sieht.
// ---------------------------------------------------------------------------

function Compare() {
  const { i18n } = useTranslation();
  const messages = useMemo(() => {
    const pool = getChatPool(i18n.language || "de");
    return getRandomWindow(pool, CHAT_WINDOW);
  }, [i18n.language]);

  return (
    <div className={styles.compare} aria-hidden>
      <ChatChaos messages={messages} />
      <OrganizedList />
    </div>
  );
}

function ChatChaos({ messages }: { messages: Array<{ person: string; text: string; side: "left" | "right" }> }) {
  const { t } = useTranslation();
  return (
    <section
      className={styles.chatCard}
      aria-label={t("home.labelBefore")}
    >
      <span className={styles.panelLabel}>{t("home.labelBefore")}</span>
      <div className={styles.chatScroll}>
        {messages.map((m, i) => (
          <div
            key={i}
            className={`${styles.chatRow} ${m.side === "right" ? styles.chatRowRight : ""}`}
          >
            {m.side === "left" && (
              <span
                className={styles.chatAvatar}
                style={{ backgroundColor: avatarColor(m.person) }}
              >
                {initials(m.person)}
              </span>
            )}
            <span
              className={`${styles.chatBubble} ${
                m.side === "right" ? styles.chatBubbleMine : ""
              }`}
            >
              {m.text}
            </span>
            {m.side === "right" && (
              <span
                className={styles.chatAvatar}
                style={{ backgroundColor: avatarColor(m.person) }}
              >
                {initials(m.person)}
              </span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function OrganizedList() {
  const { t } = useTranslation();
  const rows: Array<{ item: string; name: string | null }> = [
    { item: t("home.listItemBrot"), name: t("home.listPersonB") },
    { item: t("home.listItemDrinks"), name: t("home.listPersonC") },
    { item: t("home.listItemSalat"), name: t("home.listPersonA") },
    { item: t("home.listItemDessert"), name: t("home.listPersonD") },
    { item: t("home.listItemSausage"), name: null },
  ];
  return (
    <section
      className={styles.listCard}
      aria-label={t("home.labelAfter")}
    >
      <span className={`${styles.panelLabel} ${styles.panelLabelAfter}`}>
        {t("home.labelAfter")}
      </span>
      <ul className={styles.orgList}>
        {rows.map((row, i) => (
          <li key={i} className={styles.orgRow}>
            {row.name ? (
              <span
                className={styles.orgAvatar}
                style={{ backgroundColor: avatarColor(row.name) }}
              >
                {initials(row.name)}
              </span>
            ) : (
              <span className={styles.orgAvatarEmpty} />
            )}
            <div className={styles.orgBody}>
              <span className={styles.orgItem}>{row.item}</span>
              <span className={styles.orgSub}>
                {row.name ?? t("home.listOpen")}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Use-Cases — „Gemacht für" mit 4 Emoji-Chips.
// ---------------------------------------------------------------------------

function UseCases() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "de";
  const items = HOME_USE_CASE_IDS.map((id) => findUseCaseById(id)).filter(
    (u): u is NonNullable<ReturnType<typeof findUseCaseById>> => Boolean(u),
  );
  return (
    <section className={styles.useCases} aria-label={t("home.useCasesTitle")}>
      <h2 className={styles.useCasesTitle}>{t("home.useCasesTitle")}</h2>
      <ul className={styles.useCasesList}>
        {items.map((uc) => {
          const Icon = uc.icon;
          const content = getUseCaseContent(uc.id, lang);
          if (!content) return null;
          return (
            <li key={uc.id} className={styles.useCase}>
              <Link
                to={`/usefor/${slugFor(uc, lang)}`}
                className={`${styles.useCaseLink} ${styles[`useCase_${uc.tone}`]}`}
              >
                <span className={styles.useCaseIcon}>
                  <Icon size={26} weight="regular" />
                </span>
                <span className={styles.useCaseLabel}>{content.shortLabel}</span>
              </Link>
            </li>
          );
        })}
      </ul>
      <Link to="/usefor" className={styles.useCasesAllLink}>
        {t("useCases.allLink")} →
      </Link>
    </section>
  );
}
